import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Dumbbell, Apple, GraduationCap, Podcast, BookOpen, Mail } from "lucide-react";
import { MaskedLines, Reveal, RevealImage } from "@/components/motion";
import Marquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";
import { LINKS, IMAGES, TESTIMONIALS, PAIN_POINTS, SERVICES } from "@/data/content";

const SERVICE_ICONS = { build: Dumbbell, "food-freedom": Apple, mentorship: GraduationCap };

const Home = () => {

  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 700], [0, 90]);
  const heroImgYFast = useTransform(scrollY, [0, 700], [0, 170]);
  const heroImgYSlow = useTransform(scrollY, [0, 700], [0, 40]);
  const heroTextY = useTransform(scrollY, [0, 700], [0, -40]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-mist grain">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-32 md:pt-40 pb-16 md:pb-24">
          <div className="grid items-end gap-12 lg:grid-cols-12">
            <motion.div style={{ y: heroTextY }} className="lg:col-span-7">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="label-text text-ocean"
                data-testid="hero-eyebrow"
              >
                Strength · Food Freedom · Mindset Coaching
              </motion.p>
              <h1
                className="mt-6 font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tight"
                data-testid="hero-heading"
              >
                <span className="block overflow-hidden pb-[0.1em] -mb-[0.05em]">
                  <motion.span
                    className="block italic font-light text-ocean text-3xl sm:text-4xl lg:text-5xl tracking-normal"
                    initial={{ y: "115%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                  >
                    The Strength Movement
                  </motion.span>
                </span>
                <MaskedLines
                  lines={[
                    "Build strength.",
                    "Train with confidence.",
                  ]}
                  delay={0.35}
                />
                <span className="block overflow-hidden pb-[0.1em]">
                  <motion.span
                    className="block italic font-light text-ink/50"
                    initial={{ y: "115%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
                  >
                    Trust your body.
                  </motion.span>
                </span>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9 }}
                className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/70"
                data-testid="hero-subheading"
              >
                For women who are tired of conflicting fitness advice and want
                confidence under the barbell and around food, coaching built on
                the science of your body and the psychology of you.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.05 }}
                className="mt-10 flex flex-wrap gap-4"
              >
                <Link
                  to="/coaching"
                  data-testid="hero-work-with-me-button"
                  className="inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-ocean"
                >
                  Work with me
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
                <a
                  href={LINKS.liftingGuide}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-free-guide-button"
                  className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-ocean hover:text-ocean"
                >
                  <BookOpen size={16} strokeWidth={1.5} />
                  Download the Max Lifting Guide
                </a>
              </motion.div>
            </motion.div>

            <div className="lg:col-span-5">
              <div className="relative pb-10" data-testid="hero-image-frame">
                <motion.div
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="arch-clip relative ml-auto aspect-[4/5] w-4/5 overflow-hidden shadow-2xl shadow-ink/20"
                >
                  <motion.img
                    src={IMAGES.coaching}
                    alt="Coach Kim guiding a client through a strength session"
                    style={{ y: heroImgY, scale: 1.15 }}
                    className="h-full w-full object-cover"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 40, rotate: -6 }}
                  animate={{ opacity: 1, y: 0, rotate: -4 }}
                  transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
                  style={{ y: heroImgYFast }}
                  className="absolute -left-3 top-2 w-40 overflow-hidden rounded-xl border-4 border-white shadow-xl shadow-ink/15 md:w-56"
                  data-testid="hero-client-photo-tori"
                >
                  <img
                    src={IMAGES.clientTori}
                    alt="Tori deadlifting, Strength Movement client"
                    className="aspect-[3/4] w-full object-cover"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 40, rotate: 7 }}
                  animate={{ opacity: 1, y: 0, rotate: 5 }}
                  transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  style={{ y: heroImgYSlow }}
                  className="absolute -bottom-6 left-0 w-36 overflow-hidden rounded-full border-4 border-white shadow-xl shadow-ink/15 md:w-48"
                  data-testid="hero-client-photo-kim-lizi"
                >
                  <img
                    src="/images/community/c4.jpg"
                    alt="Kim cheering on Lizi by the barbell"
                    className="aspect-square w-full object-cover"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 40, rotate: 4 }}
                  animate={{ opacity: 1, y: 0, rotate: 2 }}
                  transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
                  style={{ y: heroImgYFast }}
                  className="absolute -right-3 -top-3 w-32 overflow-hidden rounded-xl border-4 border-white shadow-xl shadow-ink/15 md:w-44"
                  data-testid="hero-client-photo-crew"
                >
                  <img
                    src="/images/community/group-header.webp"
                    alt="The Strength Movement community crew"
                    className="aspect-[3/4] w-full object-cover"
                  />
                </motion.div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 1 }}
            className="mt-16 hidden md:flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-ink/40"
          >
            <ArrowDown size={14} strokeWidth={1.5} className="animate-bounce" />
            Scroll
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* WE UNDERSTAND */}
      <section className="bg-white" data-testid="who-this-is-for-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-36">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="label-text text-ocean">I understand</p>
                <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-balance">
                  Sound <span className="italic font-light text-ink/50">familiar?</span>
                </h2>
                <p className="mt-6 text-base md:text-lg text-ink/60 leading-relaxed">
                  You're not broken, and you're not alone. Most of the women who
                  find The Strength Movement have been here:
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="divide-y divide-ink/10 border-y border-ink/10">
                {PAIN_POINTS.map((point, i) => (
                  <Reveal key={i} delay={i * 0.06} y={16}>
                    <li className="flex items-start gap-5 py-6" data-testid={`pain-point-${i}`}>
                      <span className="font-display text-2xl italic text-ocean/70 leading-none pt-1">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="text-base md:text-lg text-ink/80">{point}</p>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* THE SCIENCE / THE PSYCHOLOGY / THE EXPERIENCE */}
      <section className="bg-ink text-white grain" data-testid="philosophy-section">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-36">
          <Reveal>
            <p className="label-text text-ocean">The Strength Movement difference</p>
            <h2 className="mt-6 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-balance">
              Most fitness advice focuses on doing more.
              <span className="italic font-light text-white/60"> I focus on understanding more.</span>
            </h2>
          </Reveal>

          <div className="mt-16 md:mt-24 grid gap-px bg-white/10 md:grid-cols-3 border border-white/10">
            {[
              {
                n: "01",
                title: "The Science",
                sub: "Understanding how your body works.",
                items: ["Strength & programming", "Movement & technique", "Nutrition for performance", "Recovery & fatigue"],
              },
              {
                n: "02",
                title: "The Psychology",
                sub: "Understanding how you work.",
                items: ["Trust & confidence", "Food & body image", "Beliefs & perfectionism", "Autonomy & self-coaching"],
              },
              {
                n: "03",
                title: "The Experience",
                sub: "Coaching as a relationship.",
                items: ["Weekly check-ins", "Video technique feedback", "Ongoing messaging", "Community & group catch ups"],
              },
            ].map((ch, i) => (
              <Reveal key={ch.n} delay={i * 0.1} className="bg-ink p-8 md:p-12">
                <div data-testid={`philosophy-chapter-${ch.n}`}>
                  <span className="font-display text-5xl md:text-6xl italic font-light text-ocean">{ch.n}</span>
                  <Link
                    to={ch.n === "01" ? "/science" : ch.n === "02" ? "/psychology" : "/coaching"}
                    data-testid={`philosophy-link-${ch.n}`}
                    className="group inline-block"
                  >
                    <h3 className="mt-6 font-display text-3xl md:text-4xl transition-colors duration-300 group-hover:text-ocean">{ch.title}</h3>
                  </Link>
                  <p className="mt-3 text-sm md:text-base text-white/60">{ch.sub}</p>
                  <ul className="mt-8 space-y-3">
                    {ch.items.map((item) => (
                      <li key={item} className="flex items-center gap-3 text-sm text-white/80">
                        <span className="h-1 w-1 rounded-full bg-ocean" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <p className="mt-16 max-w-2xl font-display text-2xl md:text-3xl italic font-light leading-snug text-white/80">
              “We don't just build stronger lifters. We build women who trust
              themselves.”
            </p>
          </Reveal>
        </div>
      </section>

      {/* MAX LIFTING DAY */}
      <section className="bg-ink text-white grain" data-testid="lifting-day-section">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <Reveal>
            <p className="label-text text-ocean">Max Lifting Day</p>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              The community, <span className="italic font-light text-white/60">in motion.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 mx-auto max-w-sm md:max-w-md" data-testid="gallery-hype-reel">
              <div className="overflow-hidden border border-white/15 shadow-2xl shadow-black/40">
                <video
                  src="/videos/hype-reel-web.mp4"
                  poster="/videos/hype-reel-poster.jpg"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  className="w-full"
                />
              </div>
              <p className="mt-4 text-center text-xs uppercase tracking-[0.25em] text-white/40">
                Max Lifting Day, the community in action
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WAYS TO WORK TOGETHER */}
      <section className="bg-mist" data-testid="services-overview-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-36">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="label-text text-ocean">Ways to work together</p>
                <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                  Ways to <span className="italic font-light text-ink/50">work with me</span>
                </h2>
              </div>
              <Link
                to="/coaching"
                data-testid="services-overview-all-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-ink transition-colors hover:text-ocean"
              >
                Explore everything
                <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.id];
              return (
                <Reveal key={s.id} delay={i * 0.1}>
                  <Link
                    to={s.id === "mentorship" ? "/mentorship" : `/coaching#${s.id}`}
                    data-testid={`service-card-${s.id}`}
                    className="group flex h-full flex-col border border-ink/10 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:border-ocean/40 hover:shadow-xl hover:shadow-ink/5"
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={IMAGES[s.image]}
                        alt={s.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <span className="absolute left-4 top-4 rounded-full bg-white/85 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-ink backdrop-blur">
                        {s.audience}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col p-7">
                      <div className="flex items-center justify-between">
                        <span className="font-display text-xl italic text-ocean/70">{s.number}</span>
                        <Icon size={20} strokeWidth={1.5} className="text-ink/40" />
                      </div>
                      <h3 className="mt-3 font-display text-3xl leading-tight">{s.name}</h3>
                      <p className="mt-2 text-sm font-semibold text-ocean">{s.tagline}</p>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/60">{s.description}</p>
                      <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-ink transition-colors group-hover:text-ocean">
                        Learn more
                        <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLIENT STORIES */}
      <section className="bg-white" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-36">
          <Reveal>
            <p className="label-text text-ocean">Client stories</p>
            <h2 className="mt-6 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-balance">
              Strength, food & confidence,
              <span className="italic font-light text-ink/50"> the wins look like this.</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={(i % 2) * 0.1}>
                <figure
                  data-testid={`testimonial-${t.name.toLowerCase()}`}
                  className="flex h-full flex-col justify-between border border-ink/10 bg-mist p-8 md:p-10 transition-colors duration-500 hover:border-ocean/30"
                >
                  <blockquote className="font-display text-xl md:text-2xl leading-snug text-ink/85">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-8 flex items-center gap-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ocean font-display text-lg italic text-white">
                      {t.name[0]}
                    </span>
                    <span>
                      <span className="block text-sm font-bold">{t.name}</span>
                      <span className="block text-xs uppercase tracking-[0.15em] text-ink/50">{t.tag}</span>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY GALLERY */}
      <section className="bg-white pb-24 md:pb-36" data-testid="community-gallery-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="label-text text-ocean">The community</p>
                <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                  The Movement <span className="italic font-light text-ink/50">in action.</span>
                </h2>
              </div>
              <p className="max-w-sm text-sm leading-relaxed text-ink/60">
                Real women, real strength, the community behind the coaching.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 columns-2 gap-4 md:columns-3">
            {[
              { src: IMAGES.clientTori, alt: "Tori squatting at a Strength Movement session", id: "tori" },
              { src: "/images/community/c4.jpg", alt: "Kim cheering on Lizi by the barbell", id: "kim-lizi" },
              { src: "/images/community/c2.jpg", alt: "Squatting deep while the crew watches on", id: "squat" },
              { src: IMAGES.coaching, alt: "Kim watching Lizi's squat up close", id: "coaching" },
              { src: "/images/community/c3.jpg", alt: "Clapping on a lifting attempt", id: "clap" },
              { src: "/images/community/c1.jpg", alt: "Laughing between attempts on squat day", id: "laugh" },
              { src: "/images/community/group-header.webp", alt: "The Strength Movement community crew", id: "crew" },
            ].map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08} className="mb-4 break-inside-avoid">
                <div
                  className="group overflow-hidden rounded-lg"
                  data-testid={`gallery-photo-${p.id}`}
                >
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FREE RESOURCES */}
      <section className="bg-mist" data-testid="resources-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-36">
          <Reveal>
            <p className="label-text text-ocean">Free resources</p>
            <h2 className="mt-6 max-w-2xl font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Start <span className="italic font-light text-ink/50">here.</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            <Reveal>
              <a
                href={LINKS.liftingGuide}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="resource-lifting-guide"
                className="group flex h-full flex-col justify-between border border-ink/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-ocean/40 hover:shadow-xl hover:shadow-ink/5"
              >
                <div>
                  <BookOpen size={24} strokeWidth={1.5} className="text-ocean" />
                  <h3 className="mt-5 font-display text-3xl">Max Lifting Guide</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">
                    The free guide to building real, lasting strength, technique,
                    programming and confidence under the bar.
                  </p>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-bold transition-colors group-hover:text-ocean">
                  Download free <ArrowUpRight size={15} strokeWidth={1.5} />
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.1}>
              <a
                href={LINKS.foodFreedomGuide}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="resource-food-freedom-guide"
                className="group flex h-full flex-col justify-between border border-ink/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-ocean/40 hover:shadow-xl hover:shadow-ink/5"
              >
                <div>
                  <Apple size={24} strokeWidth={1.5} className="text-ocean" />
                  <h3 className="mt-5 font-display text-3xl">Food Freedom Guide</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">
                    Begin untangling food rules and rebuilding trust with your
                    body, the starting point for a peaceful relationship with food.
                  </p>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-bold transition-colors group-hover:text-ocean">
                  Download free <ArrowUpRight size={15} strokeWidth={1.5} />
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex h-full flex-col justify-between border border-ink/10 bg-ink p-8 text-white">
                <div>
                  <Podcast size={24} strokeWidth={1.5} className="text-ocean" />
                  <h3 className="mt-5 font-display text-3xl">The Podcast</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">
                    The Strength Movement Podcast, real conversations on strength,
                    food, body image and building autonomy.
                  </p>
                </div>
                <div className="mt-8 flex flex-col gap-3">
                  <a
                    href={LINKS.podcast}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="resource-podcast-link"
                    className="inline-flex items-center gap-2 text-sm font-bold text-white transition-colors hover:text-ocean"
                  >
                    Listen on Spotify <ArrowUpRight size={15} strokeWidth={1.5} />
                  </a>
                  <a
                    href={LINKS.emailList}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="resource-email-list-link"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-white/60 transition-colors hover:text-white"
                  >
                    <Mail size={15} strokeWidth={1.5} /> Join the email list
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="bg-white" data-testid="about-teaser-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-36">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <RevealImage
                src="/images/community/kim-barbell.webp"
                alt="Coach Kim leaning on the barbell, smiling"
                className="arch-clip aspect-[4/5] shadow-2xl shadow-ink/10"
                testId="about-teaser-image"
              />
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <Reveal>
                <p className="label-text text-ocean">Meet your coach</p>
                <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-balance">
                  Hey, I'm <span className="italic font-light text-ocean">Kim.</span>
                </h2>
                <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-ink/70">
                  Founder and head coach of The Strength Movement. After a decade
                  of second-guessing every food and exercise choice I made, one
                  conversation about strength changed my future. Now it's my
                  mission to help women build autonomy in their choices, and
                  bridge the gap between wellness and performance.
                </p>
                <Link
                  to="/about"
                  data-testid="about-teaser-link"
                  className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-ink transition-colors hover:text-ocean"
                >
                  Read my story
                  <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default Home;
